import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  KeyRound,
  Lock,
  MapPin,
  Navigation,
  RadioTower,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  UserRound,
  Zap
} from 'lucide-react';
import { setAuthSession } from '../utils/auth';
import { getBrandImageUrl, setBrowserBranding } from '../utils/browserBranding';
import LicenseFooter from '../components/LicenseFooter';
import './Login.css';
import { API_URL } from '../config/api';

function AnimatedNetwork() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let frame;

    const resize = () => {
      canvas.width = window.innerWidth * window.devicePixelRatio;
      canvas.height = window.innerHeight * window.devicePixelRatio;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0);
    };

    const nodes = Array.from({ length: 54 }, (_, index) => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.24,
      vy: (Math.random() - 0.5) * 0.24,
      size: index % 7 === 0 ? 2.6 : 1.8,
    }));

    const draw = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = 'rgba(247, 250, 252, 0.97)';
      ctx.fillRect(0, 0, width, height);

      const band = ctx.createLinearGradient(0, 0, width, height);
      band.addColorStop(0, 'rgba(14, 165, 233, 0.08)');
      band.addColorStop(0.45, 'rgba(16, 185, 129, 0.06)');
      band.addColorStop(1, 'rgba(245, 158, 11, 0.07)');
      ctx.fillStyle = band;
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = 'rgba(15, 23, 42, 0.04)';
      ctx.lineWidth = 1;
      for (let x = 0; x < width; x += 82) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += 82) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      nodes.forEach((node, index) => {
        node.x += node.vx;
        node.y += node.vy;

        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;

        nodes.slice(index + 1).forEach((target) => {
          const distance = Math.hypot(node.x - target.x, node.y - target.y);
          if (distance < 170) {
            ctx.strokeStyle = `rgba(14, 116, 144, ${0.12 * (1 - distance / 170)})`;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(target.x, target.y);
            ctx.stroke();
          }
        });

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);
        ctx.fillStyle = index % 5 === 0 ? 'rgba(16, 185, 129, 0.68)' : 'rgba(14, 116, 144, 0.58)';
        ctx.fill();
      });

      frame = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="login-network" />;
}

function RajuMotorsBackdrop() {
  return (
    <div className="raju-login-scene" aria-hidden="true">
      <div className="raju-gps-grid" />
      <div className="raju-route route-one" />
      <div className="raju-route route-two" />
      <div className="raju-route route-three" />
      <div className="raju-gps-tower">
        <RadioTower size={30} />
        <span />
        <span />
      </div>
      <div className="raju-gps-pin pin-one"><MapPin size={22} /></div>
      <div className="raju-gps-pin pin-two"><MapPin size={18} /></div>
      <div className="raju-gps-pin pin-three"><Navigation size={20} /></div>
    </div>
  );
}

function AuthenticatorDownloadBadges() {
  return (
    <div className="login-authenticator-download-box">
      <div className="download-box-title">
        <Smartphone size={14} color="#0284c7" />
        <span>Don't have Google Authenticator app?</span>
      </div>
      <div className="download-buttons-grid">
        <a
          href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
          target="_blank"
          rel="noopener noreferrer"
          className="store-download-btn playstore-btn"
          title="Download Google Authenticator for Android on Google Play"
        >
          <svg className="store-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M3.609 1.814L13.792 12 3.61 22.186a2.373 2.373 0 0 1-.61-.924V2.738a2.37 2.37 0 0 1 .609-.924zm11.242 11.245l2.484 2.485-11.834 6.782 9.35-9.267zm2.484-2.118l-2.484 2.485-9.35-9.267 11.834 6.782zm1.06 1.06l3.328 1.906c.928.532.928 1.398 0 1.93l-3.328 1.906-2.073-2.073 2.073-2.669z"/>
          </svg>
          <div className="store-btn-text">
            <small>GET IT ON</small>
            <strong>Google Play</strong>
          </div>
        </a>

        <a
          href="https://apps.apple.com/app/google-authenticator/id388497605"
          target="_blank"
          rel="noopener noreferrer"
          className="store-download-btn appstore-btn"
          title="Download Google Authenticator for iOS on Apple App Store"
        >
          <svg className="store-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.85c.62-.75 1.04-1.8 1.01-2.85-.92.04-2.03.62-2.69 1.39-.58.67-.99 1.73-.85 2.76 1.03.08 2.07-.55 2.53-1.3z"/>
          </svg>
          <div className="store-btn-text">
            <small>Download on</small>
            <strong>App Store</strong>
          </div>
        </a>
      </div>
    </div>
  );
}

const defaultBrand = {
  brand_name: 'BizTrack CRM',
  primary_color: '#0284c7',
  secondary_color: '#0f172a',
  login_eyebrow: 'BizTrack Access',
  login_title: 'Welcome back',
  login_subtitle: 'Secure GPS & Fleet Operations CRM',
  login_primary_action: 'Login to Portal',
  login_secure_label: 'End-to-End Encrypted',
  login_access_label: '24/7 High Availability',
  theme_mode: 'light'
};

const isRajuMotorsProfile = (brand) => {
  const name = String(brand?.brand_name || '').toLowerCase();
  const domain = String(brand?.custom_domain || '').toLowerCase();
  return name.includes('raju') || domain.includes('raju');
};

const getPortalKey = () => {
  if (typeof window === 'undefined') return '';
  const searchParams = new URLSearchParams(window.location.search);
  const wlId = searchParams.get('wl') || searchParams.get('admin_id') || '';
  const host = window.location.host || '';
  return `wl_brand_${host}_${wlId}`;
};

const isPotentialWhitelabelPortal = () => {
  if (typeof window === 'undefined') return false;
  const searchParams = new URLSearchParams(window.location.search);
  const wlId = searchParams.get('wl') || searchParams.get('admin_id');
  const host = window.location.host.toLowerCase();
  return Boolean(wlId || (!host.includes('localhost') && !host.includes('biztrack') && !host.includes('credyou')));
};

const getInitialBrandState = () => {
  try {
    const key = getPortalKey();
    const cached = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && (parsed.brand_name || parsed.logo_url)) {
        return { brand: { ...defaultBrand, ...parsed }, isLoaded: true };
      }
    }
  } catch (e) {}

  if (isPotentialWhitelabelPortal()) {
    return { brand: null, isLoaded: false };
  }

  return { brand: defaultBrand, isLoaded: true };
};

export default function Login() {
  const initial = useMemo(() => getInitialBrandState(), []);
  const [brand, setBrand] = useState(initial.brand);
  const [brandLoaded, setBrandLoaded] = useState(initial.isLoaded);
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // 2FA state machine: 'credentials' | '2fa_setup' | '2fa_verify'
  const [authStep, setAuthStep] = useState('credentials');
  const [twoFAData, setTwoFAData] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [copiedSecret, setCopiedSecret] = useState(false);

  const navigate = useNavigate();
  const brandLogo = getBrandImageUrl(brand?.logo_url);
  const isRajuMotors = isRajuMotorsProfile(brand);

  useEffect(() => {
    if (!brand) return;
    const brandName = brand.brand_name || defaultBrand.brand_name;
    setBrowserBranding({
      title: `${brandName} Login`,
      icon: brandLogo,
      fallbackText: brandName,
      themeColor: brand.primary_color || defaultBrand.primary_color
    });
  }, [brand, brandLogo]);

  useEffect(() => {
    const fetchBrand = async () => {
      try {
        const params = new URLSearchParams();
        params.set('host', window.location.host);
        const portalAdminId = new URLSearchParams(window.location.search).get('wl')
          || new URLSearchParams(window.location.search).get('admin_id');
        if (portalAdminId) params.set('admin_id', portalAdminId);
        const res = await axios.get(`${API_URL}/api/workspace/whitelabel/public?${params.toString()}`);
        const brandData = res.data || {};
        const mergedBrand = { ...defaultBrand, ...brandData };
        setBrand(mergedBrand);
        setBrandLoaded(true);

        try {
          const key = getPortalKey();
          if (key) localStorage.setItem(key, JSON.stringify(brandData));
        } catch (e) {}
      } catch (err) {
        console.log(err);
        setBrand(defaultBrand);
        setBrandLoaded(true);
      }
    };
    fetchBrand();
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const host = typeof window !== 'undefined' ? window.location.host : '';
      const portalAdminId = brand?.admin_id
        || new URLSearchParams(window.location.search).get('wl')
        || new URLSearchParams(window.location.search).get('admin_id')
        || '';
      const res = await axios.post(`${API_URL}/api/auth/login`, { mobile, password, host, portal_admin_id: portalAdminId });
      
      if (res.data.require_2fa) {
        setTwoFAData(res.data);
        setAuthStep(res.data.step === 'setup' ? '2fa_setup' : '2fa_verify');
        setOtpCode('');
        return;
      }

      const brandSession = brand?.admin_id ? {
        brand_name: brand.brand_name || '',
        brand_logo_url: brand.logo_url || '',
        brand_portal_url: brand.portal_url || '',
        brand_primary_color: brand.primary_color || '',
        brand_secondary_color: brand.secondary_color || '',
        brand_context_active: 1,
        brand_owner_profile_pic: brand.owner_profile_pic || ''
      } : {};

      const sessionUser = setAuthSession({ token: res.data.token, user: { ...res.data.user, ...brandSession } });
      if (!sessionUser) throw new Error('Login succeeded, but user details were not returned.');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Login failed. Please check credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const brandSession = brand?.admin_id ? {
        brand_name: brand.brand_name || '',
        brand_logo_url: brand.logo_url || '',
        brand_portal_url: brand.portal_url || '',
        brand_primary_color: brand.primary_color || '',
        brand_secondary_color: brand.secondary_color || '',
        brand_context_active: 1,
        brand_owner_profile_pic: brand.owner_profile_pic || ''
      } : {};

      const res = await axios.post(`${API_URL}/api/auth/2fa/verify`, {
        temp_token: twoFAData?.temp_token,
        code: otpCode
      });

      const sessionUser = setAuthSession({ token: res.data.token, user: { ...res.data.user, ...brandSession } });
      if (!sessionUser) throw new Error('2FA verified, but user session could not be established.');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Invalid 6-digit code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = () => {
    if (twoFAData?.secret) {
      navigator.clipboard.writeText(twoFAData.secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  const resetToCredentials = () => {
    setAuthStep('credentials');
    setTwoFAData(null);
    setOtpCode('');
    setError('');
  };

  if (!brandLoaded || !brand) {
    return (
      <main className="login-shell" style={{ '--login-primary': '#0284c7', '--login-secondary': '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0f172a' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ width: '42px', height: '42px', margin: '0 auto 14px', border: '3px solid rgba(255,255,255,0.12)', borderTopColor: '#38bdf8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </main>
    );
  }

  return (
    <main className={`login-shell ${brand.theme_mode === 'dark' ? 'login-dark' : ''} ${isRajuMotors ? 'login-vehicle-theme' : ''}`} style={{ '--login-primary': brand.primary_color, '--login-secondary': brand.secondary_color }}>
      {isRajuMotors ? <RajuMotorsBackdrop /> : <AnimatedNetwork />}
      <section className="login-stage login-single-panel">
        <motion.div
          className="login-card"
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="login-brand">
            {brand.logo_url ? (
              <div className="login-brand-badge">
                <img className="login-brand-logo" src={brandLogo} alt={brand.brand_name || 'Brand Logo'} />
              </div>
            ) : (
              <img className="login-brand-logo" src={brandLogo || '/assets/BizTrack_Logo.png'} alt={brand.brand_name || 'BizTrack'} />
            )}
          </div>

          {/* STEP 1: USERNAME/PASSWORD FORM */}
          {authStep === 'credentials' && (
            <form onSubmit={handleLogin} style={{ display: 'contents' }}>
              <div className="login-copy">
                <span className="login-eyebrow"><ShieldCheck size={15} /> {brand.login_eyebrow || `${brand.brand_name} Access`}</span>
                <h1>{brand.login_title || 'Welcome back'}</h1>
                <p>{brand.login_subtitle || defaultBrand.login_subtitle}</p>
              </div>

              {error && <div className="login-error">{error}</div>}

              <label className="login-field">
                <span>Username / Mobile</span>
                <div>
                  <UserRound size={18} />
                  <input
                    type="text"
                    value={mobile}
                    onChange={(event) => setMobile(event.target.value)}
                    placeholder="Username or registered mobile number"
                    required
                  />
                </div>
              </label>

              <label className="login-field">
                <span>Password</span>
                <div>
                  <Lock size={18} />
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Account password"
                    required
                  />
                </div>
              </label>

              <button className="login-submit" type="submit" disabled={loading}>
                {loading ? 'Authenticating...' : (brand.login_primary_action || defaultBrand.login_primary_action)}
                <ArrowRight size={18} />
              </button>

              <div className="login-trust-row">
                <span><Lock size={14} /> {brand.login_secure_label || defaultBrand.login_secure_label}</span>
                <span><Zap size={14} /> {brand.login_access_label || defaultBrand.login_access_label}</span>
              </div>

              <AuthenticatorDownloadBadges />
            </form>
          )}

          {/* STEP 2A: FIRST-TIME 2FA SETUP (QR CODE + SECRET KEY) */}
          {authStep === '2fa_setup' && (
            <form onSubmit={handleVerify2FA} style={{ display: 'contents' }}>
              <div className="login-copy">
                <span className="login-eyebrow" style={{ color: '#0284c7' }}>
                  <ShieldAlert size={15} /> Two-Factor Security Setup
                </span>
                <h1>Set Up Authenticator</h1>
                <p>Scan this QR code with Google Authenticator or enter key manually:</p>
              </div>

              {error && <div className="login-error">{error}</div>}

              <div className="login-2fa-setup-card">
                {twoFAData?.qr_code && (
                  <div className="login-qr-container">
                    <img src={twoFAData.qr_code} alt="2FA QR Code" className="login-qr-img" />
                  </div>
                )}

                <div className="login-secret-wrapper">
                  <span className="login-secret-code">{twoFAData?.secret}</span>
                  <button
                    type="button"
                    onClick={handleCopySecret}
                    className="login-copy-btn"
                  >
                    {copiedSecret ? <Check size={12} /> : <Copy size={12} />}
                    {copiedSecret ? 'Copied' : 'Copy Key'}
                  </button>
                </div>
              </div>

              <label className="login-field login-otp-field">
                <span>Enter 6-Digit Code from App *</span>
                <div>
                  <KeyRound size={18} />
                  <input
                    type="text"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    inputMode="numeric"
                    autoFocus
                    value={otpCode}
                    onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    required
                  />
                </div>
              </label>

              <button className="login-submit" type="submit" disabled={loading || otpCode.length !== 6}>
                {loading ? 'Activating 2FA...' : 'Verify & Activate 2FA'}
                <ArrowRight size={18} />
              </button>

              <button type="button" onClick={resetToCredentials} className="login-back-action">
                <ArrowLeft size={14} /> Back to Login
              </button>

              <AuthenticatorDownloadBadges />
            </form>
          )}

          {/* STEP 2B: SUBSEQUENT LOGIN 2FA VERIFICATION */}
          {authStep === '2fa_verify' && (
            <form onSubmit={handleVerify2FA} style={{ display: 'contents' }}>
              <div className="login-copy">
                <span className="login-eyebrow" style={{ color: '#0284c7' }}>
                  <ShieldCheck size={15} /> 2FA Verification
                </span>
                <h1>Security Check</h1>
                <p>Enter the 6-digit code from Google Authenticator for <strong>{twoFAData?.account_name}</strong>:</p>
              </div>

              {error && <div className="login-error">{error}</div>}

              <label className="login-field login-otp-field" style={{ marginTop: '10px' }}>
                <span>6-Digit Authenticator Code *</span>
                <div>
                  <KeyRound size={18} />
                  <input
                    type="text"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    inputMode="numeric"
                    autoFocus
                    value={otpCode}
                    onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    required
                  />
                </div>
              </label>

              <button className="login-submit" type="submit" disabled={loading || otpCode.length !== 6}>
                {loading ? 'Verifying...' : 'Verify & Login'}
                <ArrowRight size={18} />
              </button>

              <button type="button" onClick={resetToCredentials} className="login-back-action">
                <ArrowLeft size={14} /> Back to Login
              </button>

              <AuthenticatorDownloadBadges />
            </form>
          )}
        </motion.div>
      </section>
      <LicenseFooter className="login-license-footer" />
    </main>
  );
}
